import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

const UTM_TS_MAX_AGE_SECONDS = 5 * 60; // 5 minutes timeout

type UtmTrackingParams = {
    isValid: boolean;
    params: Record<string, string | null>;
};

function stripUtmParams(searchParams: URLSearchParams): URLSearchParams {
    const next = new URLSearchParams(searchParams);
    Array.from(next.keys()).forEach((key) => {
        if (key.toLowerCase().startsWith('utm_')) {
            next.delete(key);
        }
    });
    return next;
}

function validateUtmTimestamp(timestamp: string | null): boolean {
    if (!timestamp) {
        return false;
    }
    const ts = parseInt(timestamp, 10);
    if (Number.isNaN(ts)) {
        return false;
    }
    // Validate if timestamp is too old or in the future to 'fake' the redirect (E.g. somehow user was able to save the URL with the UTM params and is trying to use it later)
    // The timestamp also creates a unique ID for the redirect, so we can track if the same redirect is used multiple times.
    const nowSeconds = Math.floor(Date.now() / 1000);
    const age = nowSeconds - ts;
    if (age < 0 || age > UTM_TS_MAX_AGE_SECONDS) {
        return false;
    }
    return true;
}

/**
 * Provides callbacks to capture UTM redirect payload from the URL and to strip utm_* params.
 */
export function useUtmTracking() {
    const [searchParams, setSearchParams] = useSearchParams();

    const getUtmParams = useCallback((): UtmTrackingParams => {
        const utmSource = searchParams.get('utm_source');
        const utmTs = searchParams.get('utm_ts');
        // Emitted under non-`utm_` names on purpose: PostHog's `before_send` sanitizer
        // deletes every `utm_*` property as a campaign key, which would strip the payload
        // this event exists to record.
        return {
            isValid: !!utmSource && !!utmTs && validateUtmTimestamp(utmTs),
            params: { redirect_source: utmSource, redirect_ts: utmTs },
        };
    }, [searchParams]);

    const cleanUtmParamsFromUrl = useCallback(() => {
        const nextParams = stripUtmParams(searchParams);
        setSearchParams(nextParams, { replace: true });
    }, [searchParams, setSearchParams]);

    return { getUtmParams, cleanUtmParamsFromUrl };
}
